import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
  Stack,
  Alert,
  AlertDescription,
  Button,
} from "@altimateai/lego";

interface Failure {
  row: number;
  reason: string;
}

interface Props {
  title: string;
  created?: number;
  skipped?: number;
  reactivated?: number;
  failures?: Failure[];
  submittedAt?: number;
}

export const SSOUploadStatusModal = ({
  title,
  created,
  skipped,
  reactivated,
  failures,
  submittedAt,
}: Props) => {
  const [open, setOpen] = useState(false);
  const prevSubmittedAt = useRef(submittedAt);

  useEffect(() => {
    if (submittedAt && submittedAt !== prevSubmittedAt.current) {
      prevSubmittedAt.current = submittedAt;
      setOpen(true);
    }
  }, [submittedAt]);

  const hasResults = created !== undefined || (failures && failures.length > 0);
  if (!hasResults) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="al-max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="al-space-y-4">
          {(created ?? 0) > 0 && (
            <Alert>
              <AlertDescription>
                {created} created
                {(skipped ?? 0) > 0 && `, ${skipped} skipped`}
                {(reactivated ?? 0) > 0 && `, ${reactivated} reactivated`}
              </AlertDescription>
            </Alert>
          )}
          {created === 0 && !failures?.length && (
            <Alert>
              <AlertDescription>No new items to process.</AlertDescription>
            </Alert>
          )}
          {failures && failures.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                <Typography variant="body" className="al-font-medium al-mb-2">
                  {failures.length} row{failures.length !== 1 ? "s" : ""} failed:
                </Typography>
                <div className="al-max-h-48 al-overflow-y-auto al-space-y-1">
                  {failures.map(f => (
                    <div key={f.row} className="al-text-xs">
                      Row {f.row}: {f.reason}
                    </div>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}
          <Stack direction="row" className="al-justify-end">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Close
            </Button>
          </Stack>
        </div>
      </DialogContent>
    </Dialog>
  );
};
